import React from "react";

import {
  ArrowRight,
  CheckCircle2,
  FileCheck2,
  LoaderCircle,
  LockKeyhole,
  Mail,
  ShieldCheck,
  TriangleAlert,
  UserRound,
  LogOut,
} from "lucide-react";

import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "../../../components/LanguageSwitcher";
import backgroundImg from "../../../assets/background.png";
import logoImg from "../../../assets/logo.png";

import "./HomebuyerInvitationPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const AUTH_CONNECTION_KEY = "legup_auth_connection";

const HOMEBUYER_CONNECTION = "LEGUP-Homebuyers";

function InvitationPageFrame({ children, compact = false }) {
  return (
    <div className="hb-invitation-page">
      <div
        className="hb-invitation-page__bg-illustration"
        style={{
          backgroundImage: `url(${backgroundImg})`,
        }}
      />

      <header className="hb-invitation-page__topbar">
        <Link to="/" className="hb-invitation-page__brand" aria-label="LEG-UP">
          <img src={logoImg} alt="LEG-UP" />
        </Link>

        <LanguageSwitcher />
      </header>

      <main
        className={`hb-invitation-page__shell ${
          compact ? "hb-invitation-page__shell--compact" : ""
        }`}
      >
        {children}
      </main>
    </div>
  );
}

/* =========================================================
   HOMEBUYER INVITATION PAGE
   ========================================================= */

export default function HomebuyerInvitationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("homebuyer-invitation");

  /* =========================================================
     AUTH0
     ========================================================= */

  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  /* =========================================================
     STATE
     ========================================================= */

  const [invitation, setInvitation] = React.useState(null);

  const [loadingInvitation, setLoadingInvitation] = React.useState(true);

  const [accepting, setAccepting] = React.useState(false);

  const [accepted, setAccepted] = React.useState(false);

  const [error, setError] = React.useState("");

  /* =========================================================
     NORMALISED EMAILS
     ========================================================= */

  const invitedEmail = String(invitation?.email || "")
    .trim()
    .toLowerCase();

  const authenticatedEmail = String(user?.email || "")
    .trim()
    .toLowerCase();

  const homebuyerAccountExists = Boolean(invitation?.homebuyer_account_exists);

  const activeAuthConnection =
    typeof window !== "undefined"
      ? window.sessionStorage.getItem(AUTH_CONNECTION_KEY) || ""
      : "";

  const isAuthenticatedAsHomebuyer = Boolean(
    isAuthenticated && activeAuthConnection === HOMEBUYER_CONNECTION,
  );

  /* =========================================================
     AUTHENTICATED ACCOUNT STATE
     ========================================================= */

  const hasMatchingAuthenticatedAccount = Boolean(
    homebuyerAccountExists &&
    isAuthenticatedAsHomebuyer &&
    invitedEmail &&
    authenticatedEmail &&
    invitedEmail === authenticatedEmail,
  );

  const hasDifferentAuthenticatedAccount = Boolean(
    homebuyerAccountExists &&
    isAuthenticatedAsHomebuyer &&
    invitedEmail &&
    authenticatedEmail &&
    invitedEmail !== authenticatedEmail,
  );

  /* =========================================================
     INVITATION NAME
     ========================================================= */

  const applicantName = [invitation?.first_name, invitation?.last_name]
    .filter(Boolean)
    .join(" ");

  /* =========================================================
     VALIDATE INVITATION
     ========================================================= */

  React.useEffect(() => {
    let cancelled = false;

    async function validateInvitation() {
      if (!token) {
        setError(
          t("errors.missingToken", "The invitation link is incomplete."),
        );

        setLoadingInvitation(false);

        return;
      }

      try {
        setLoadingInvitation(true);
        setError("");

        const response = await fetch(
          `${API_BASE}/api/homeowner/invitations/${encodeURIComponent(
            token,
          )}/validate/`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t(
                "errors.invalidInvitation",
                "This invitation is not valid or is no longer active.",
              ),
          );
        }

        if (!cancelled) {
          setInvitation(data);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError?.message ||
              t(
                "errors.validationFailed",
                "The invitation could not be validated.",
              ),
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingInvitation(false);
        }
      }
    }

    validateInvitation();

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  /* =========================================================
     NEW HOMEBUYER REGISTRATION
     ========================================================= */

  function handleRegistration() {
    if (!token) {
      return;
    }

    navigate(`/register/homebuyer?invitation=${encodeURIComponent(token)}`);
  }

  /* =========================================================
     EXISTING HOMEBUYER LOGIN
     ========================================================= */

  async function handleExistingAccountLogin() {
    if (!token) {
      return;
    }

    window.sessionStorage.setItem(AUTH_CONNECTION_KEY, HOMEBUYER_CONNECTION);

    await loginWithRedirect({
      appState: {
        returnTo: `/invitation/${token}`,
      },

      authorizationParams: {
        prompt: "login",

        connection: HOMEBUYER_CONNECTION,

        ...(invitation?.email
          ? {
              login_hint: invitation.email,
            }
          : {}),
      },
    });
  }

  /* =========================================================
     SWITCH ACCOUNT
     ========================================================= */

  function handleSwitchAccount() {
    if (!token) {
      return;
    }

    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/invitation/${token}`,
      },
    });
  }

  /* =========================================================
     ACCEPT EXISTING ACCOUNT INVITATION
     ========================================================= */

  async function acceptInvitationForExistingAccount() {
    if (!token || accepting) {
      return;
    }

    try {
      setAccepting(true);
      setError("");

      let accessToken = "";

      try {
        accessToken = await getAccessTokenSilently();
      } catch (authError) {
        const authErrorCode = String(authError?.error || "")
          .trim()
          .toLowerCase();

        const authMessage = String(authError?.message || "")
          .trim()
          .toLowerCase();

        const requiresLogin =
          authErrorCode === "login_required" ||
          authErrorCode === "consent_required" ||
          authMessage.includes("login required");

        if (requiresLogin) {
          await loginWithRedirect({
            appState: {
              returnTo: `/invitation/${token}`,
            },
            authorizationParams: {
              screen_hint: "login",
              ...(invitation?.email
                ? {
                    login_hint: invitation.email,
                  }
                : {}),
            },
          });

          return;
        }

        throw authError;
      }

      const response = await fetch(
        `${API_BASE}/api/homeowner/invitations/${encodeURIComponent(
          token,
        )}/accept/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            t("errors.acceptFailed", "The invitation could not be accepted."),
        );
      }

      setAccepted(true);

      window.setTimeout(() => {
        navigate("/hb/dashboard", {
          replace: true,
          state: {
            invitedFlow: true,
            invitationId: data?.invitation_id || null,
            dossierId: data?.dossier_id || null,
            dossierCode: data?.dossier_code || "",
            showFirstEntryMessage: Boolean(data?.show_first_entry_message),
          },
        });
      }, 1100);
    } catch (requestError) {
      setError(
        requestError?.message ||
          t("errors.acceptFailed", "The invitation could not be accepted."),
      );
    } finally {
      setAccepting(false);
    }
  }

  async function handleAccept() {
    if (!token || accepting) {
      return;
    }

    if (!homebuyerAccountExists) {
      handleRegistration();

      return;
    }

    if (!isAuthenticatedAsHomebuyer) {
      await handleExistingAccountLogin();

      return;
    }

    if (hasDifferentAuthenticatedAccount) {
      return;
    }

    if (!hasMatchingAuthenticatedAccount) {
      setError(
        t(
          "errors.accountCouldNotBeVerified",
          "We could not verify the Homebuyer " +
            "account for this invitation. " +
            "Please sign in again using the " +
            "invited Homebuyer account.",
        ),
      );

      return;
    }

    await acceptInvitationForExistingAccount();
  }

  /* =========================================================
     LOADING
     ========================================================= */

  const isLoading = authLoading || loadingInvitation;

  if (isLoading) {
    return (
      <InvitationPageFrame compact>
        <section className="hb-invitation-page__card hb-invitation-page__card--state">
          <div className="hb-invitation-page__loader">
            <LoaderCircle size={30} />
          </div>

          <span className="hb-invitation-page__eyebrow">
            {t("loading.eyebrow", "Secure invitation")}
          </span>

          <h1>{t("loading.title", "Checking your invitation")}</h1>

          <p className="hb-invitation-page__lead">
            {t(
              "loading.description",
              "Please wait while we securely verify the invitation.",
            )}
          </p>
        </section>
      </InvitationPageFrame>
    );
  }

  /* =========================================================
     INVALID INVITATION
     ========================================================= */

  if (error && !invitation) {
    return (
      <InvitationPageFrame compact>
        <section className="hb-invitation-page__card hb-invitation-page__card--state">
          <div className="hb-invitation-page__status-icon hb-invitation-page__status-icon--error">
            <TriangleAlert size={29} />
          </div>

          <span className="hb-invitation-page__eyebrow">
            {t("invalid.eyebrow", "Invitation unavailable")}
          </span>

          <h1>{t("invalid.title", "We could not open this invitation")}</h1>

          <p className="hb-invitation-page__lead">{error}</p>

          <div className="hb-invitation-page__state-divider" />

          <p className="hb-invitation-page__secondary-copy">
            {t(
              "invalid.help",
              "The invitation may have expired, already been accepted, or the link may be incorrect.",
            )}
          </p>
        </section>
      </InvitationPageFrame>
    );
  }

  /* =========================================================
     ACCEPTED
     ========================================================= */

  if (accepted) {
    return (
      <InvitationPageFrame compact>
        <section className="hb-invitation-page__card hb-invitation-page__card--state">
          <div className="hb-invitation-page__success-ring">
            <CheckCircle2 size={33} />
          </div>

          <span className="hb-invitation-page__eyebrow">
            {t("success.eyebrow", "Invitation accepted")}
          </span>

          <h1>{t("success.title", "Your loan case is now connected")}</h1>

          <p className="hb-invitation-page__lead">
            {t(
              "success.description",
              "We are taking you to your LEG-UP dashboard.",
            )}
          </p>

          <div className="hb-invitation-page__redirect">
            <LoaderCircle size={16} />

            <span>{t("success.redirecting", "Opening your dashboard...")}</span>
          </div>
        </section>
      </InvitationPageFrame>
    );
  }

  /* =========================================================
     MAIN PAGE
     ========================================================= */

  return (
    <InvitationPageFrame>
      <section className="hb-invitation-page__card">
        <header className="hb-invitation-page__card-header">
          <div className="hb-invitation-page__status">
            <ShieldCheck size={14} />

            <span>{t("badge", "Secure invitation")}</span>
          </div>
        </header>

        <div className="hb-invitation-page__hero">
          <div className="hb-invitation-page__hero-icon">
            <Mail size={26} />
          </div>

          <span className="hb-invitation-page__eyebrow">
            {t("eyebrow", "Homebuyer invitation")}
          </span>

          <h1>
            {applicantName
              ? t("titleWithName", "Welcome, {{name}}", {
                  name: applicantName,
                })
              : t("title", "You have been invited to LEG-UP")}
          </h1>

          <p className="hb-invitation-page__lead">
            {t(
              "description",
              "A lender has prepared a loan case for you. Connect your LEG-UP account to securely access the case and continue your renovation journey.",
            )}
          </p>
        </div>

        <div className="hb-invitation-page__details">
          <div className="hb-invitation-page__detail">
            <div className="hb-invitation-page__detail-icon">
              <FileCheck2 size={18} />
            </div>

            <div>
              <span>{t("details.caseReference", "Dossier reference")}</span>

              <strong>{invitation?.dossier_code || "—"}</strong>
            </div>
          </div>

          <div className="hb-invitation-page__detail">
            <div className="hb-invitation-page__detail-icon">
              <Mail size={18} />
            </div>

            <div>
              <span>{t("details.invitedEmail", "Invited email")}</span>

              <strong>{invitation?.email || "—"}</strong>
            </div>
          </div>
        </div>

        <div className="hb-invitation-page__info">
          <LockKeyhole size={18} />

          <p>
            {t(
              "security",
              "For your security, the account you use must have the same email address as this invitation.",
            )}
          </p>
        </div>

        {hasDifferentAuthenticatedAccount ? (
          <div className="hb-invitation-page__error" role="alert">
            <TriangleAlert size={17} />

            <span>
              {t(
                "errors.loggedInAsDifferentUser",
                "This invitation was sent to {{invitedEmail}}, but you are currently signed in as {{currentEmail}}.",
                {
                  invitedEmail: invitation?.email || "",
                  currentEmail: user?.email || "",
                },
              )}
            </span>
          </div>
        ) : null}

        {error && !hasDifferentAuthenticatedAccount ? (
          <div className="hb-invitation-page__error" role="alert">
            <TriangleAlert size={17} />

            <span>{error}</span>
          </div>
        ) : null}

        <div className="hb-invitation-page__actions">
          {!homebuyerAccountExists ? (
            <>
              <button
                type="button"
                className={
                  "hb-invitation-page__button " +
                  "hb-invitation-page__button--primary"
                }
                onClick={handleRegistration}
              >
                <CheckCircle2 size={17} />

                <span>
                  {t(
                    "actions.continueRegistration",
                    "Continue to Homebuyer registration",
                  )}
                </span>

                <ArrowRight size={17} />
              </button>

              <p className="hb-invitation-page__action-help">
                {t(
                  "actions.registrationRequired",
                  "You will first complete your LEG-UP " +
                    "Homebuyer registration. Information " +
                    "already provided by your lender will " +
                    "be prefilled.",
                )}
              </p>
            </>
          ) : hasMatchingAuthenticatedAccount ? (
            <>
              <div className="hb-invitation-page__signed-in">
                <UserRound size={16} />

                <span>
                  {t("account.signedInAs", "Signed in as {{email}}", {
                    email: user?.email || "",
                  })}
                </span>
              </div>

              <button
                type="button"
                className={
                  "hb-invitation-page__button " +
                  "hb-invitation-page__button--primary"
                }
                onClick={handleAccept}
                disabled={accepting}
              >
                {accepting ? (
                  <LoaderCircle
                    size={17}
                    className="hb-invitation-page__spinner"
                  />
                ) : (
                  <CheckCircle2 size={17} />
                )}

                <span>
                  {accepting
                    ? t("actions.accepting", "Connecting your account...")
                    : t("actions.accept", "Accept invitation")}
                </span>

                {!accepting ? <ArrowRight size={17} /> : null}
              </button>

              <p className="hb-invitation-page__action-help">
                {t(
                  "actions.acceptHelp",
                  "By accepting, this lender-created " +
                    "dossier will be connected to your " +
                    "Homebuyer account.",
                )}
              </p>
            </>
          ) : (
            <>
              <button
                type="button"
                className={
                  "hb-invitation-page__button " +
                  "hb-invitation-page__button--primary"
                }
                onClick={handleExistingAccountLogin}
              >
                <UserRound size={17} />

                <span>
                  {t(
                    "actions.signInHomebuyer",
                    "Sign in with your Homebuyer account",
                  )}
                </span>

                <ArrowRight size={17} />
              </button>

              <p className="hb-invitation-page__action-help">
                {t(
                  "actions.homebuyerLoginRequired",
                  "A Homebuyer account already exists " +
                    "for this email. Sign in through " +
                    "the Homebuyer connection to accept " +
                    "the invitation.",
                )}
              </p>
            </>
          )}
        </div>

        <footer className="hb-invitation-page__footer">
          <ShieldCheck size={14} />

          <span>
            {t(
              "footer",
              "Your information is handled securely within the LEG-UP platform.",
            )}
          </span>
        </footer>
      </section>
    </InvitationPageFrame>
  );
}
